"use client";

import { useQuery } from "@tanstack/react-query";
import client from "@/lib/api";
import { ApiError } from "@/lib/api-error";
import { queryKeys } from "@/lib/query-keys";
import type { Appointment } from "@/types";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isAppointmentId(value: string | undefined): value is string {
  return !!value && UUID_PATTERN.test(value);
}

export function useAppointmentDetail(
  id: string | undefined,
  initialData?: Appointment
) {
  const validId = isAppointmentId(id);
  const query = useQuery({
    queryKey: queryKeys.appointments.detail(id ?? ""),
    enabled: validId,
    initialData: validId ? initialData : undefined,
    initialDataUpdatedAt: initialData ? 0 : undefined,
    meta: { suppressGlobalError: true },
    queryFn: async ({ signal }) => {
      try {
        const { data } = await client.GET("/appointments/{id}", {
          params: { path: { id: id as string } },
          signal,
        });
        return data ?? null;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
  });

  const fatalError = validId && query.isError && query.data === undefined;

  return {
    appointment: query.data ?? null,
    isLoading: validId && query.isLoading,
    isFetching: query.isFetching,
    isError: fatalError,
    isNotFound: validId && query.isSuccess && query.data === null,
    refetch: query.refetch,
  };
}
